'use client';
import { useSearchParams } from 'next/navigation';
import { useState, useEffect, ReactNode } from 'react';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { ChannelStripSkeleton } from '@/ui-components/molecules/ChannelStripSkeleton/ChannelStripSkeleton';
import { Select, Option, OptionGroup } from '@/ui-components/atoms/Inputs/Select';
import { NativeShowMore } from '@/ui-components/molecules/NativeShowMore/NativeShowMore';
import ChannelList from './ChannelList';
import ChannelHeader from './ChannelHeader';
import GenreSelector from './GenreSelector';
import YmmTrigger from './YmmTrigger';
import DownloadLink from './DownloadLink';
import { LockIcon } from '@/assets/icons/LockIcon';
import { ChannelGuideProps } from './interfaces';

import { RadioSelect } from '@/ui-components/molecules/RadioSelect/RadioSelect';
import { PodcastList } from './PodcastList';
import { useGetPodcasts } from '@/hooks/useGetPodcasts';
import { useChannelGuideData } from '@/hooks/useChannelGuideData';
import { useTabUrlSync } from '@/hooks/useTabUrlSync';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { getChannelGuideTaxonomy } from './utils';
import { resolveChannelGuideSelection } from './selection';

import styles from './channelGuide.module.scss';
import cn from 'classnames';

const {
    headerAreaWrapper,
    tabsSelectorWrapper,
    selectorsWrapper,
    selectorStyles,
    selectOption,
    showMoreTextStyles,
    showMore,
    showMoreButton,
    showMoreAreaStyles,
    selectPlanHeaderSubTitleStyles,
    channelGuideContentWrapper,
    channelGuideSkeletonWrapper,
    radioSelectContainer,
} = styles;

function createSpaceHolder(): ReactNode[] {
    return Array.from(Array(8).keys()).map((num) => {
        return (
            <div className={channelGuideSkeletonWrapper} key={num}>
                <ChannelStripSkeleton />
            </div>
        );
    });
}

const TABS = {
    podcasts: 'podcasts',
    channels: 'channels',
} as const;

const TAB_PARAM = 'tab';

type TabsValue = (typeof TABS)[keyof typeof TABS];

interface Props extends ChannelGuideProps {
    renderedAt: number;
}

export default function ChannelGuide({ asset, pageContext, children, hasStickyBar, renderedAt }: Props) {
    const searchParams = useSearchParams();
    const allPackages = asset.fields.allPackages;
    const lineupLink = asset.fields.channelLineup;
    const initialSelection = resolveChannelGuideSelection(
        searchParams.get('category'),
        searchParams.get('genre'),
        getChannelGuideTaxonomy(asset.fields),
    );
    const [bodyComponent, setBodyComponent] = useState(initialSelection.category);
    const [genre, setGenre] = useState(initialSelection.genre);
    const [randomNumber, setRandom] = useState<number>(0);
    const [label, setLabel] = useState(initialSelection.genreLabel);
    const [removeFirstBatch, setRemoveFirstBatch] = useState(false);

    const {
        packageId,
        setPackageId,
        channels,
        loading,
        taxonomy,
        channelButtons,
        getPackageById,
        handleChannelSelection,
    } = useChannelGuideData({
        asset,
        pageContext,
        onFetchStart: () => setRemoveFirstBatch(true),
    });

    const podcastIds = asset.fields.podcasts;
    const hasPodcasts = podcastIds && podcastIds.length > 0;

    const validTabs = Object.values(TABS).filter((tab) => tab !== TABS.podcasts || hasPodcasts);
    const [selectedTab, setSelectedTab] = useTabUrlSync(TAB_PARAM, validTabs, TABS.channels);

    const {
        podcasts,
        isLoadingPodcasts,
        isPendingPodcasts,
        onChangePodcastCategory,
        podcastCategory,
        resetPodcasts,
        fetchPodcastsFirstPage,
        loadMorePodcasts,
    } = useGetPodcasts(asset.cms_id);

    useEffect(() => {
        if (selectedTab === TABS.podcasts && isPendingPodcasts) {
            fetchPodcastsFirstPage();
        }
    }, [selectedTab, fetchPodcastsFirstPage, isPendingPodcasts]);

    function handleShowMore() {
        setTimeout(() => setRandom(Math.random()), 1);
    }

    function selectHeader(type: string) {
        return {
            title: `Select a ${type}`,
            body:
                type !== 'genre' ? (
                    <div className={selectPlanHeaderSubTitleStyles}>
                        <span>Channels not included in the plan will appear locked</span>
                        <span aria-hidden="true">
                            {' '}
                            (<LockIcon />)
                        </span>
                    </div>
                ) : null,
        };
    }

    function plansSelector() {
        if (!packageId && !channels) {
            return;
        }

        const mainPackages = allPackages.slice(0, allPackages.length - asset.fields.showMorePackageCount);
        const morePackages = allPackages.slice(allPackages.length - asset.fields.showMorePackageCount);

        const id = packageId || allPackages[0].fields.sms_package_id;
        const sxmSelectedPackage = id ? getPackageById(id) : null;

        return (
            <Select
                type="dropdown"
                label="Plan"
                theme="grayLightWeb2"
                className={selectorStyles}
                value={id}
                placeholder={sxmSelectedPackage?.fields.package_display_name}
                name="siriusxm package"
                onChange={(e) => setPackageId(e.value)}
                dataCy="channel-guide-package-select"
                recalculateHeight={randomNumber}
                selectHeader={selectHeader('plan')}
            >
                <OptionGroup>
                    {mainPackages.map((sxmPackage) => {
                        const packageId = sxmPackage.fields.sms_package_id;
                        if (sxmPackage.fields.package_display_name && packageId) {
                            return (
                                <Option key={packageId} value={packageId} className={selectOption}>
                                    {sxmPackage.fields.package_display_name}
                                </Option>
                            );
                        }

                        return <></>;
                    })}
                </OptionGroup>
                {morePackages.length > 0 ? (
                    <NativeShowMore
                        id="cgPackagesShowMore"
                        showText={<span className={showMoreTextStyles}>More plans</span>}
                        hideText={<span className={showMoreTextStyles}>Show less</span>}
                        dataLinkname="Toggle:More plans"
                        onToggle={handleShowMore}
                        activeToggleTextPosition="bottom"
                        className={showMore}
                        summaryClassName={showMoreButton}
                        labelClassName={showMoreAreaStyles}
                    >
                        <OptionGroup>
                            {morePackages.map((sxmPackage, i) => {
                                if (sxmPackage.fields.package_display_name && sxmPackage.fields.sms_package_id) {
                                    return (
                                        <Option
                                            key={i}
                                            value={sxmPackage.fields.sms_package_id}
                                            className={selectOption}
                                        >
                                            {sxmPackage.fields.package_display_name}
                                        </Option>
                                    );
                                }

                                return <></>;
                            })}
                        </OptionGroup>
                    </NativeShowMore>
                ) : (
                    <></>
                )}
            </Select>
        );
    }

    function changeBodyContent(data: string) {
        setBodyComponent(data);
        setGenre('All');
        if (!removeFirstBatch) {
            setRemoveFirstBatch(true);
        }
    }

    const tabs = [
        { label: 'Channels', value: TABS.channels },
        {
            label: 'Podcasts',
            value: TABS.podcasts,
            onHover: () => {
                if (!isPendingPodcasts) return;
                fetchPodcastsFirstPage();
            },
        },
    ];

    function handleTabChange(value: TabsValue) {
        if (value === TABS.channels) {
            // cache first page when switching away
            resetPodcasts(1);
        }

        setSelectedTab(value);
    }

    const disclaimer = asset.fields.podcasts_disclaimer;

    const disclaimerEl = disclaimer ? <RenderRTE contentfulDoc={disclaimer} /> : null;

    return (
        <>
            <Grid container className={styles.channelGuidePaddings}>
                <Grid col={{ sm: 12 }}>
                    {hasPodcasts ? (
                        <div
                            className={cn(radioSelectContainer, {
                                [styles.hasStickyBar]: hasStickyBar,
                            })}
                        >
                            <RadioSelect options={tabs} value={selectedTab} onChange={handleTabChange} />
                        </div>
                    ) : null}
                    {selectedTab === TABS.channels && (
                        <>
                            <div
                                className={cn(headerAreaWrapper, {
                                    [styles.hasStickyBar]: hasStickyBar,
                                })}
                            >
                                <div className={tabsSelectorWrapper}>
                                    <ChannelHeader
                                        value={bodyComponent}
                                        onClick={changeBodyContent}
                                        channelButtons={channelButtons}
                                    />
                                    <DownloadLink lineupLink={lineupLink} type="desktop" />
                                </div>
                                <div className={selectorsWrapper}>
                                    {plansSelector()}
                                    <GenreSelector
                                        bodyComponent={bodyComponent}
                                        genre={genre}
                                        label={label}
                                        taxonomy={taxonomy}
                                        hasXtraChannels={Boolean(channels?.[bodyComponent]?.AllX.length)}
                                        setGenre={setGenre}
                                        setLabel={setLabel}
                                    />
                                </div>
                                <YmmTrigger
                                    packageId={packageId}
                                    channels={channels}
                                    allPackages={allPackages}
                                    getPackageById={getPackageById}
                                />
                            </div>
                            <div className={channelGuideContentWrapper}>
                                {loading ? (
                                    <>{createSpaceHolder()}</>
                                ) : (
                                    <ChannelList
                                        channels={handleChannelSelection(bodyComponent, genre)}
                                        upsellLink={asset?.fields?.upsellLink}
                                        defaultChannelImage={asset?.fields?.channelDefaultImage}
                                        platform={pageContext?.platform || ''}
                                        removeFirstBatch={removeFirstBatch}
                                        renderedAt={renderedAt}
                                    >
                                        {children}
                                    </ChannelList>
                                )}
                            </div>
                            <DownloadLink lineupLink={lineupLink} type="mobile" />
                        </>
                    )}
                    {selectedTab === TABS.podcasts && (
                        <PodcastList
                            categorySelect={{
                                options: asset.fields.podcastCategoryGenre.map((category) => ({
                                    value: category.categoryId || '',
                                    label: category.categoryName || '',
                                })),

                                value: podcastCategory,
                                onChange: onChangePodcastCategory,
                            }}
                            podcasts={podcasts}
                            disclaimer={disclaimerEl}
                            isLoading={isPendingPodcasts || isLoadingPodcasts}
                            defaultChannelImage={asset?.fields?.channelDefaultImage}
                            onBottomReached={loadMorePodcasts}
                            hasStickyBar={hasStickyBar}
                        />
                    )}
                </Grid>
            </Grid>
        </>
    );
}
